export enum RiskScoreType {
  Base500 = "Base500",
  Base1000 = "Base1000",
  OperationalPropertyInspection = "OperationalPropertyInspection",
  OperationalPropertyReportNotes = "OperationalPropertyReportNotes",
  OperationalPropertySOP = "OperationalPropertySOP",
  OperationalPropertyConstructionData = "OperationalPropertyConstructionData",
  OperationalPropertyTotal = "OperationalPropertyTotal",
  OperationalPropertyInspectionBase2000 = "OperationalPropertyInspectionBase2000",
  OperationalPropertyReportNotesBase2000 = "OperationalPropertyReportNotesBase2000",
  OperationalPropertySOPBase2000 = "OperationalPropertySOPBase2000",
  OperationalPropertyConstructionDataBase2000 = "OperationalPropertyConstructionDataBase2000",
  OperationalPropertyTotalBase2000 = "OperationalPropertyTotalBase2000",
}

export enum RiskScoreMaxValue {
  Base500 = 500,
  OperationalPropertyReportNotes = 700,
  OperationalPropertyInspection = 250,
  OperationalPropertySOP = 200,
  OperationalPropertyConstructionData = 500,
  Base1000 = 1000,
  OperationalPropertyTotal = 1000,
  OperationalPropertySOPBase2000 = 380,
  OperationalPropertyConstructionDataBase2000 = 680,
  Base2000 = 2000,
  OperationalPropertyTotalBase2000 = 2000,
}

export interface GetRiskScoreNameParams {
  riskScore: number;
  riskScoreMax: number;
  isOperationalProperty: boolean;
  riskScoreType?: RiskScoreType;
  source?: string;
}

export interface RiskScoreLegend {
  [key: string]: {
    name: string;
    color: string;
  };
}

export const LEGEND = (
  maxScore: number,
  isOperationalProperty: boolean,
): RiskScoreLegend => {
  if (maxScore === 500 && !isOperationalProperty) {
    return {
      veryLow: {
        name: "Very Low",
        color: "#66FF00",
      },
      low: {
        name: "Low",
        color: "#03A737",
      },
      moderate: {
        name: "Moderate",
        color: "#FFF200",
      },
      high: {
        name: "High",
        color: "#FFCB05",
      },
      veryHigh: {
        name: "Very High",
        color: "#FA7000",
      },
      extreme: {
        name: "Extreme",
        color: "#FF2620",
      },
    };
  }

  return {
    veryLow: {
      name: "Very Low",
      color: "#66FF00",
    },
    low: {
      name: "Low",
      color: "#03A737",
    },
    moderate: {
      name: "Moderate",
      color: "#FFF200",
    },
    high: {
      name: "High",
      color: "#FFCB05",
    },
    veryHigh: {
      name: "Very High",
      color: "#FA7000",
    },
    extreme: {
      name: "Extreme",
      color: "#FF2620",
    },
    catastrophic: {
      name: "Catastrophic",
      color: "#b11226",
    },
  };
};

export const getRiskScoreMaxBasedOnType = (
  riskScoreType: RiskScoreType,
): number => {
  switch (riskScoreType) {
    case RiskScoreType.Base500:
      return RiskScoreMaxValue.Base500;
    case RiskScoreType.OperationalPropertyReportNotes:
      return RiskScoreMaxValue.OperationalPropertyReportNotes;
    case RiskScoreType.OperationalPropertyInspection:
      return RiskScoreMaxValue.OperationalPropertyInspection;
    case RiskScoreType.OperationalPropertySOP:
      return RiskScoreMaxValue.OperationalPropertySOP;
    case RiskScoreType.OperationalPropertyConstructionData:
      return RiskScoreMaxValue.OperationalPropertyConstructionData;
    case RiskScoreType.OperationalPropertyTotalBase2000:
      return RiskScoreMaxValue.OperationalPropertyTotalBase2000;
    case RiskScoreType.OperationalPropertySOPBase2000:
      return RiskScoreMaxValue.OperationalPropertySOPBase2000;
    case RiskScoreType.OperationalPropertyConstructionDataBase2000:
      return RiskScoreMaxValue.OperationalPropertyConstructionDataBase2000;
    case RiskScoreType.Base1000:
    case RiskScoreType.OperationalPropertyTotal:
    default:
      return RiskScoreMaxValue.Base1000;
  }
};

export const getRiskScoreNamesBasedOnType = (
  riskScore: number,
  riskScoreType: RiskScoreType,
): string => {
  if (riskScoreType === RiskScoreType.Base500) {
    switch (true) {
      case riskScore <= 50:
        return "veryLow";
      case riskScore >= 51 && riskScore <= 150:
        return "low";
      case riskScore >= 151 && riskScore <= 250:
        return "moderate";
      case riskScore >= 251 && riskScore <= 350:
        return "high";
      case riskScore >= 351 && riskScore <= 450:
        return "veryHigh";
      case riskScore >= 451:
        return "extreme";
      default:
        return "";
    }
  }
  if (riskScoreType === RiskScoreType.OperationalPropertyConstructionData) {
    switch (true) {
      case riskScore <= 71.43:
        return "veryLow";
      case riskScore >= 71.44 && riskScore <= 142.86:
        return "low";
      case riskScore >= 142.87 && riskScore <= 214.29:
        return "moderate";
      case riskScore >= 214.3 && riskScore <= 285.71:
        return "high";
      case riskScore >= 285.72 && riskScore <= 357.14:
        return "veryHigh";
      case riskScore >= 357.15 && riskScore <= 428.57:
        return "extreme";
      case riskScore >= 428.58:
        return "catastrophic";
      default:
        return "";
    }
  }
  if (riskScoreType === RiskScoreType.OperationalPropertyConstructionDataBase2000) {
    switch (true) {
      // 622 is the max score for Construction Data, so the thresholds are calculated based on that
      case riskScore <= 88.85:
        return "veryLow";
      case riskScore >= 88.86 && riskScore <= 177.71:
        return "low";
      case riskScore >= 177.72 && riskScore <= 266.57:
        return "moderate";
      case riskScore >= 266.58 && riskScore <= 355.43:
        return "high";
      case riskScore >= 355.44 && riskScore <= 444.29:
        return "veryHigh";
      case riskScore >= 444.3 && riskScore <= 533.14:
        return "extreme";
      case riskScore >= 533.15:
        return "catastrophic";
      default:
        return "";
    }
  }
  if (riskScoreType === RiskScoreType.OperationalPropertySOP) {
    switch (true) {
      case riskScore <= 28.57:
        return "veryLow";
      case riskScore >= 28.58 && riskScore <= 57.14:
        return "low";
      case riskScore >= 57.15 && riskScore <= 85.71:
        return "moderate";
      case riskScore >= 85.72 && riskScore <= 114.29:
        return "high";
      case riskScore >= 114.3 && riskScore <= 142.86:
        return "veryHigh";
      case riskScore >= 142.87 && riskScore <= 171.43:
        return "extreme";
      case riskScore >= 171.44:
        return "catastrophic";
      default:
        return "";
    }
  }
  if (riskScoreType === RiskScoreType.OperationalPropertySOPBase2000) {
    switch (true) {
      // 380 is the max score for SOP, so the thresholds are calculated based on that
      case riskScore <= 54.28:
        return "veryLow";
      case riskScore >= 54.29 && riskScore <= 114.29:
        return "low";
      case riskScore >= 114.3 && riskScore <= 171.43:
        return "moderate";
      case riskScore >= 171.44 && riskScore <= 228.57:
        return "high";
      case riskScore >= 228.58 && riskScore <= 285.71:
        return "veryHigh";
      case riskScore >= 285.72 && riskScore <= 342.86:
        return "extreme";
      case riskScore >= 342.87:
        return "catastrophic";
      default:
        return "";
    }
  }
  if (riskScoreType === RiskScoreType.OperationalPropertyTotal) {
    switch (true) {
      case riskScore <= 142.86:
        return "veryLow";
      case riskScore >= 142.87 && riskScore <= 285.71:
        return "low";
      case riskScore >= 285.72 && riskScore <= 428.57:
        return "moderate";
      case riskScore >= 428.58 && riskScore <= 571.43:
        return "high";
      case riskScore >= 571.44 && riskScore <= 714.29:
        return "veryHigh";
      case riskScore >= 714.3 && riskScore <= 857.14:
        return "extreme";
      case riskScore >= 857.15:
        return "catastrophic";
      default:
        return "";
    }
  }
  switch (true) {
    case riskScore <= 100:
      return "veryLow";
    case riskScore >= 101 && riskScore <= 200:
      return "low";
    case riskScore >= 201 && riskScore <= 300:
      return "moderate";
    case riskScore >= 301 && riskScore <= 400:
      return "high";
    case riskScore >= 401 && riskScore <= 500:
      return "veryHigh";
    case riskScore >= 501 && riskScore <= 700:
      return "extreme";
    default:
      return "catastrophic";
  }
};

export const getRiskScoreName = ({
  riskScore,
  riskScoreMax,
  isOperationalProperty,
  riskScoreType,
}: GetRiskScoreNameParams): string => {
  if (riskScoreType !== undefined) {
    console.log(riskScoreMax);
    if (riskScoreMax === 2000) {
      const base20RiskScore = `${riskScoreType}Base2000` as RiskScoreType;

      return getRiskScoreNamesBasedOnType(riskScore, base20RiskScore);
    }
    const type =
      isOperationalProperty && riskScoreType === RiskScoreType.Base1000
        ? RiskScoreType.OperationalPropertyTotal
        : riskScoreType;
    const riskScoreName = getRiskScoreNamesBasedOnType(riskScore, type);

    return riskScoreName;
  }
  if (riskScoreMax === 500 && !isOperationalProperty) {
    switch (true) {
      case riskScore <= 50:
        return "veryLow";
      case riskScore >= 51 && riskScore <= 150:
        return "low";
      case riskScore >= 151 && riskScore <= 250:
        return "moderate";
      case riskScore >= 251 && riskScore <= 350:
        return "high";
      case riskScore >= 351 && riskScore <= 450:
        return "veryHigh";
      case riskScore >= 451:
        return "extreme";
      default:
        return "";
    }
  }
  if (isOperationalProperty && riskScoreMax === 500) {
    switch (true) {
      case riskScore <= 71.43:
        return "veryLow";
      case riskScore >= 71.44 && riskScore <= 142.86:
        return "low";
      case riskScore >= 142.87 && riskScore <= 214.29:
        return "moderate";
      case riskScore >= 214.3 && riskScore <= 285.71:
        return "high";
      case riskScore >= 285.72 && riskScore <= 357.14:
        return "veryHigh";
      case riskScore >= 357.15 && riskScore <= 428.57:
        return "extreme";
      case riskScore >= 428.58:
        return "catastrophic";
      default:
        return "";
    }
  }
  switch (true) {
    case riskScore <= 100:
      return "veryLow";
    case riskScore >= 101 && riskScore <= 200:
      return "low";
    case riskScore >= 201 && riskScore <= 300:
      return "moderate";
    case riskScore >= 301 && riskScore <= 400:
      return "high";
    case riskScore >= 401 && riskScore <= 500:
      return "veryHigh";
    case riskScore >= 501 && riskScore <= 700:
      return "extreme";
    default:
      return "catastrophic";
  }
};
